// 底线提醒 — checks one side's 择偶要求 against the other side's 档案资料.
// Pure (no React), so scripts/bottom-line-selftest can run it under node.
//
//   req     fid -> raw stored value of the member whose requirements are checked
//           (profile_global_custom_values, keyed by global_custom_fields.id)
//   self    that member's own facts (only religion is read: 宗教必须一致)
//   other   the other person's facts: { age, height, love, child, pay, edu, house, religion }
//           as the raw English option values stored in the profile
//
// A 画像 answer is stored in three tiers {ideal, acceptable, bottom_line}. They
// are levels of acceptance, not narrowing filters: bottom_line is the least the
// member will still accept (理想「有房」, 底线「没有房」 = no home is still OK).
// So a person is acceptable if ANY tier takes them, and falling outside all
// three is level 'bottom' (碰底线) — or 'pref' when no 底线 tier was filled.
// A plain value from the older profile form
// has no tiers; it is the member's stated 择偶要求 and a miss is level 'pref'
// (不符合择偶要求). Anything unknown on either side ("Prefer not to say",
// blank, unparseable) is skipped — the reminder only fires on a definite miss.

const parseJson = (s) => {
  if (typeof s !== 'string') return s;
  const v = s.trim();
  if (!/^[[{"]/.test(v)) return s;
  try { return JSON.parse(v); } catch (e) { return s; }
};

const isTiered = (v) => v && typeof v === 'object' && !Array.isArray(v)
  && ('ideal' in v || 'acceptable' in v || 'bottom_line' in v);

const blank = (v) => v == null || (typeof v === 'string' && v.trim() === '')
  || (Array.isArray(v) && v.length === 0);

// The requirement as a list of accepted answers (one per filled tier) and how
// strong a miss is, or null.
function requirement(raw) {
  const v = parseJson(raw);
  if (blank(v)) return null;
  if (isTiered(v)) {
    const tiers = [v.ideal, v.acceptable, v.bottom_line].map(parseJson).filter(x => !blank(x));
    // No 底线 tier filled → the member never drew a bottom line, only a wish.
    return tiers.length ? { values: tiers, level: blank(parseJson(v.bottom_line)) ? 'pref' : 'bottom' } : null;
  }
  return { values: [v], level: 'pref' };
}

// Checkbox values arrive as a JSON array, an array of comma-joined strings, or
// one comma-joined string. None of the options checked here contain commas.
function list(v) {
  const out = [];
  const walk = (x) => {
    const p = parseJson(x);
    if (Array.isArray(p)) { p.forEach(walk); return; }
    if (p == null) return;
    String(p).split(',').map(s => s.trim()).filter(Boolean).forEach(s => out.push(s));
  };
  walk(v);
  return [...new Set(out)];
}

function range(v) {
  const p = parseJson(v);
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  const num = (x) => (x === '' || x == null || !Number.isFinite(Number(x)) ? null : Number(x));
  const lo = num(p.start); const hi = num(p.end);
  return lo == null && hi == null ? null : { lo, hi };
}

// The span every tier together accepts; an open end on any tier opens it.
function rangeOf(values) {
  const rs = values.map(range).filter(Boolean);
  if (!rs.length) return null;
  const lo = rs.some(r => r.lo == null) ? null : Math.min(...rs.map(r => r.lo));
  const hi = rs.some(r => r.hi == null) ? null : Math.max(...rs.map(r => r.hi));
  return lo == null && hi == null ? null : { lo, hi };
}

// The lowest 最低学历 / 最低月薪 any tier accepts; "No Preference" anywhere = none.
function minRank(values, table) {
  const ranks = [];
  for (const v of values) {
    const k = low(list(v)[0]);
    if (!table[k]) return null;
    ranks.push(table[k]);
  }
  return ranks.length ? Math.min(...ranks) : null;
}

const rangeText = ({ lo, hi }, unit) => (lo != null && hi != null ? `${lo}–${hi}${unit}`
  : lo != null ? `${lo}${unit}以上` : `${hi}${unit}以下`);

// ── option tables (stored English → 中文 / order) ───────────────────────────
const EDU_RANK = {
  'middle school': 1, 'high school': 2, 'some college undergraduate': 3, 'some college': 4,
  'college/university': 4, "master's degree undergraduate": 5, "master's degree": 6, 'doctoral degree': 7,
};
const EDU_ZH = { 1: '初中', 2: '高中', 3: '大学肄业', 4: '大学', 5: '硕士肄业', 6: '硕士', 7: '博士' };
const PAY_RANK = { 'under $5k': 1, '$5k - $15k': 2, '$15k - $30k': 3, '$30k - $50k': 4, 'over $50k': 5 };
const PAY_ZH = { 1: '少于5k', 2: '5k~15k', 3: '15k~30k', 4: '30k~50k', 5: '50k以上' };

// mate_love option → profile `love` values it accepts
const LOVE = {
  unmarried: ['unmarried'], divorcing: ['divorcing (in progress)'], divorced: ['divorced'],
  widowed: ['widowed'], separated: ['separated'],
};
const LOVE_ZH = { unmarried: '未婚', 'divorcing (in progress)': '离婚（处理中）', divorced: '离婚', widowed: '丧偶', separated: '分居' };

// mate_child option → profile `child` values it accepts
const CHILD = {
  'no children': ['none'],
  'has children (with them)': ['yes (living with me)', 'both parties'],
  'has children (with other)': ['yes (living with other parent)', 'both parties'],
  'has children (independent)': ['yes (independent)'],
};
const CHILD_ZH = {
  none: '没有子女', 'yes (living with me)': '有子女（随自己）', 'yes (living with other parent)': '有子女（随对方）',
  'yes (independent)': '有子女（已独立）', 'both parties': '有子女（双方带）',
};

// mate_house option → profile `real_estate` values it accepts. Owning a home
// also satisfies 有购房能力.
const HOUSE = {
  'owns home': ['have house', 'have multiple houses'],
  'no home': ["don't have house"],
  'owns abroad': ['have house in another country'],
  'lives with parents': ['live with parent'],
  'able to buy': ['able to buy house', 'have house', 'have multiple houses'],
};
const HOUSE_ZH = {
  'have house': '有房', "don't have house": '没房', 'have house in another country': '有房在国外',
  'live with parent': '与父母同住', 'able to buy house': '有购房能力', 'have multiple houses': '房子多套',
};
const HOUSE_REQ_ZH = { 'owns home': '有房', 'no home': '没有房', 'owns abroad': '有房在国外', 'lives with parents': '与父母同住', 'able to buy': '有购房能力' };

const RELIGION_ZH = {
  christianity: '基督宗教', islam: '伊斯兰教', hinduism: '印度教', buddhism: '佛教', judaism: '犹太教',
  'no religion': '无宗教信仰', catholic: '天主教',
};

const low = (s) => String(s == null ? '' : s).trim().toLowerCase();

// One checkbox requirement: the other person's value must be accepted by at
// least one ticked option. Ticking every option is no requirement at all.
function checkList(values, have, table) {
  const want = [...new Set(values.flatMap(list).map(low))].filter(w => table[w]);
  if (!want.length || want.length >= Object.keys(table).length) return null;
  const h = low(have);
  if (!h || !Object.values(table).some(vals => vals.includes(h))) return null; // unknown / 暂不分享
  return want.some(w => table[w].includes(h)) ? null : want;
}

// Field ids (global_custom_fields.id) of the requirements checked.
export const BOTTOM_LINE_FIELDS = {
  age: 118, height: 119, edu: 121, pay: 122, house: 123, love: 124, child: 126, religion: 24929,
};

export function bottomLineHits(req, self, other) {
  const get = (fid) => (req ? (req[fid] != null ? req[fid] : req[String(fid)]) : undefined);
  const o = other || {};
  const hits = [];
  const add = (key, label, r, want, have) => hits.push({ key, label, level: r.level, want, have });
  const F = BOTTOM_LINE_FIELDS;

  let r = requirement(get(F.age));
  const age = Number(o.age);
  if (r && Number.isFinite(age) && age > 0) {
    const g = rangeOf(r.values);
    if (g && ((g.lo != null && age < g.lo) || (g.hi != null && age > g.hi))) add('age', '年龄', r, rangeText(g, ' 岁'), `${age} 岁`);
  }

  r = requirement(get(F.height));
  const height = Number(o.height);
  if (r && Number.isFinite(height) && height >= 100) {
    const g = rangeOf(r.values);
    if (g && ((g.lo != null && height < g.lo) || (g.hi != null && height > g.hi))) add('height', '身高', r, rangeText(g, 'cm'), `${height}cm`);
  }

  r = requirement(get(F.edu));
  if (r) {
    const min = minRank(r.values, EDU_RANK);
    const have = EDU_RANK[low(o.edu)];
    if (min && have && have < min) add('edu', '学历', r, `${EDU_ZH[min]}及以上`, EDU_ZH[have]);
  }

  r = requirement(get(F.pay));
  if (r) {
    const min = minRank(r.values, PAY_RANK);
    const have = PAY_RANK[low(o.pay)];
    if (min && have && have < min) add('pay', '月薪', r, `${PAY_ZH[min]}及以上`, PAY_ZH[have]);
  }

  r = requirement(get(F.love));
  if (r) {
    const want = checkList(r.values, o.love, LOVE);
    if (want) add('love', '婚姻', r, want.map(w => LOVE_ZH[LOVE[w][0]]).join('、'), LOVE_ZH[low(o.love)]);
  }

  r = requirement(get(F.child));
  if (r) {
    const want = checkList(r.values, o.child, CHILD);
    const wantZh = { 'no children': '没有子女', 'has children (with them)': '有子女（随自己）', 'has children (with other)': '有子女（随对方）', 'has children (independent)': '有子女（已独立）' };
    if (want) add('child', '子女', r, want.map(w => wantZh[w]).join('、'), CHILD_ZH[low(o.child)]);
  }

  r = requirement(get(F.house));
  if (r) {
    const want = checkList(r.values, o.house, HOUSE);
    if (want) add('house', '房产', r, want.map(w => HOUSE_REQ_ZH[w]).join('、'), HOUSE_ZH[low(o.house)]);
  }

  r = requirement(get(F.religion));
  // 宗教必须一致 only when every filled tier says so.
  if (r && r.values.every(v => low(list(v)[0]) === 'must be the same')) {
    const a = low(self && self.religion); const b = low(o.religion);
    if (RELIGION_ZH[a] && RELIGION_ZH[b] && a !== b) add('religion', '宗教', r, `必须一致（${RELIGION_ZH[a]}）`, RELIGION_ZH[b]);
  }

  return hits;
}

// Age in whole years from a DATE / ISO string, or null.
export function ageFromDob(dob, now = new Date()) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  let a = now.getUTCFullYear() - d.getUTCFullYear();
  const m = now.getUTCMonth() - d.getUTCMonth();
  if (m < 0 || (m === 0 && now.getUTCDate() < d.getUTCDate())) a--;
  return a > 0 && a < 120 ? a : null;
}

// 「年龄 要 30–45 岁，对方 52 岁」 — `who` names the person whose fact missed.
export const hitText = (h, who = '对方') => `${h.label} 要 ${h.want}，${who} ${h.have}`;
